import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowRight, Sparkles, BookOpen, Wrench, Palette, Trophy, ShieldCheck, IdCard,
    Brain, Building2, Users, Heart, Star, CheckCircle2, GraduationCap, Rocket, Briefcase,
    TrendingUp, PlayCircle, ChevronRight,
} from "lucide-react";
import PublicShell from "@/components/public/PublicShell";
import LeadDialog from "@/components/public/LeadDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import api from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1758270705172-07b53627dfcb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHw0fHxzdHVkZW50cyUyMHRlY2glMjB3b3Jrc2hvcCUyMGxlYXJuaW5nJTIwbGFwdG9wJTIwY2xhc3Nyb29tfGVufDB8fHx8MTc4OTQxMjI0Mnww&ixlib=rb-4.1.0&q=85";

const PHILOSOPHY = [
    { icon: BookOpen, label: "Learn", desc: "Understand the concept clearly." },
    { icon: Wrench, label: "Practice", desc: "Use the skill with guidance." },
    { icon: Palette, label: "Create", desc: "Build something real." },
    { icon: Trophy, label: "Demonstrate", desc: "Show evidence of competency." },
    { icon: TrendingUp, label: "Progress", desc: "Move to the next capability." },
];

const PATH_ICONS = { sparkles: Sparkles, palette: Palette, rocket: Rocket, brain: Brain, briefcase: Briefcase, "trending-up": TrendingUp, heart: Heart };

const WHY = [
    { icon: Brain, title: "Practical AI Education", desc: "Learn to use AI responsibly and productively — not just talk about it." },
    { icon: Trophy, title: "Evidence, Not Just Videos", desc: "Every learner builds real projects and demonstrates competency." },
    { icon: IdCard, title: "Verified Digital Passport", desc: "A shareable, verifiable record of your skills and experience." },
    { icon: Users, title: "Mentor-Reviewed Work", desc: "Real feedback from mentors on every major project you submit." },
];

export default function Home() {
    const [programs, setPrograms] = useState([]);
    const [paths, setPaths] = useState([]);
    const [tests, setTests] = useState([]);
    const [faqs, setFaqs] = useState([]);
    const [stats, setStats] = useState({ learners: 0, programs: 0, certificates: 0, community_reached: 0 });

    useEffect(() => {
        api.get("/programs").then((r) => setPrograms(r.data)).catch(() => {});
        api.get("/learning-paths").then((r) => setPaths(r.data)).catch(() => {});
        api.get("/testimonials").then((r) => setTests(r.data)).catch(() => {});
        api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => {});
        api.get("/site/stats").then((r) => setStats(r.data)).catch(() => {});
    }, []);

    return (
        <PublicShell>
            {/* HERO */}
            <section className="relative overflow-hidden grid-bg">
                <div className="absolute inset-0 bg-gradient-to-b from-background/40 to-background" />
                <div className="container-page relative py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-6">
                        <Badge className="rounded-full bg-accent/15 text-accent-foreground border-accent/30 hover:bg-accent/15 mb-5" data-testid="hero-badge">
                            <Sparkles className="mr-1.5 h-3.5 w-3.5 text-accent" /> Experience &gt; Certificate
                        </Badge>
                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
                            Practical Digital & <span className="text-primary">AI Education</span> for Every Generation
                        </h1>
                        <p className="mt-6 text-lg text-foreground/70 leading-relaxed max-w-xl">
                            Learn technology. Practice with technology. Create with technology. Use AI responsibly and become digitally independent.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Button asChild size="lg" className="rounded-full text-base h-12 px-6" data-testid="hero-find-path-btn">
                                <Link to="/learning-paths">Find Your Learning Path <ArrowRight className="ml-2 h-5 w-5" /></Link>
                            </Button>
                            <LeadDialog
                                source="free_assessment"
                                title="Take a Free Digital Skills Assessment"
                                trigger={
                                    <Button size="lg" variant="outline" className="rounded-full text-base h-12 px-6" data-testid="hero-assessment-btn">
                                        <PlayCircle className="mr-2 h-5 w-5" /> Free Skills Assessment
                                    </Button>
                                }
                            />
                        </div>
                        <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
                            {[["learners", "Learners"], ["programs", "Programs"], ["certificates", "Credentials"]].map(([k, l]) => (
                                <div key={k}>
                                    <p className="font-heading text-2xl sm:text-3xl font-extrabold text-primary">{(stats[k] || 0).toLocaleString()}+</p>
                                    <p className="text-xs uppercase tracking-wide text-foreground/50 mt-1">{l}</p>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                    <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }} className="lg:col-span-6">
                        <div className="relative">
                            <img src={HERO_IMG} alt="Students learning technology in a modern classroom" className="rounded-3xl shadow-2xl w-full object-cover aspect-[4/3]" />
                            <div className="absolute -bottom-5 -left-5 hidden sm:flex items-center gap-3 rounded-2xl bg-card border border-border shadow-xl p-4">
                                <span className="grid h-11 w-11 place-items-center rounded-xl bg-success/15 text-success"><ShieldCheck className="h-5 w-5" /></span>
                                <div>
                                    <p className="text-sm font-semibold">Responsible AI</p>
                                    <p className="text-xs text-foreground/60">Verify · Protect · Think</p>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* WHY */}
            <section className="section-pad">
                <div className="container-page">
                    <div className="max-w-2xl">
                        <p className="overline">Why LARUTA</p>
                        <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
                            We don't sell certificates. We build real, demonstrable skills.
                        </h2>
                    </div>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {WHY.map((w) => (
                            <Card key={w.title} className="p-6 hover:shadow-lg hover:-translate-y-1 transition-all border-border/70">
                                <span className="grid h-12 w-12 place-items-center rounded-xl bg-primary/10 text-primary"><w.icon className="h-6 w-6" /></span>
                                <h3 className="mt-4 font-heading text-lg font-semibold">{w.title}</h3>
                                <p className="mt-2 text-sm text-foreground/60 leading-relaxed">{w.desc}</p>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* PHILOSOPHY */}
            <section className="section-pad bg-secondary text-secondary-foreground">
                <div className="container-page">
                    <div className="text-center max-w-2xl mx-auto">
                        <p className="overline text-accent">The LARUTA Method</p>
                        <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold">Learn → Practice → Create → Demonstrate → Progress</h2>
                    </div>
                    <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                        {PHILOSOPHY.map((p, i) => (
                            <div key={p.label} className="relative rounded-2xl bg-white/5 border border-white/10 p-6 text-center">
                                <span className="grid h-12 w-12 mx-auto place-items-center rounded-full bg-primary text-primary-foreground"><p.icon className="h-5 w-5" /></span>
                                <p className="mt-4 font-heading text-lg font-bold">{p.label}</p>
                                <p className="mt-1.5 text-xs text-secondary-foreground/60">{p.desc}</p>
                                <span className="absolute top-3 right-4 font-mono text-xs text-white/25">0{i + 1}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PROGRAMS */}
            <section className="section-pad" id="programs">
                <div className="container-page">
                    <div className="flex flex-wrap items-end justify-between gap-4">
                        <div className="max-w-2xl">
                            <p className="overline">Flagship Programs</p>
                            <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Career programs built around real projects</h2>
                        </div>
                        <Button asChild variant="ghost" className="text-primary" data-testid="view-all-programs-btn">
                            <Link to="/programs">View all programs <ArrowRight className="ml-1 h-4 w-4" /></Link>
                        </Button>
                    </div>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {programs.map((p) => (
                            <Card key={p.slug} data-testid={`program-card-${p.slug}`} className="group flex flex-col overflow-hidden border-border/70 hover:shadow-xl hover:-translate-y-1 transition-all">
                                <div className="p-6 flex-1">
                                    <div className="flex items-center justify-between">
                                        <Badge variant="secondary" className="rounded-full">{p.badge}</Badge>
                                        <span className="text-xs text-foreground/50 font-medium">{p.duration}</span>
                                    </div>
                                    <h3 className="mt-4 font-heading text-xl font-bold group-hover:text-primary transition-colors">{p.title}</h3>
                                    <p className="mt-2 text-sm text-foreground/60 leading-relaxed line-clamp-2">{p.description}</p>
                                    <div className="mt-4 flex flex-wrap gap-1.5">
                                        {(p.skills || []).slice(0, 3).map((s) => (
                                            <span key={s} className="text-xs rounded-full bg-muted px-2.5 py-1 text-foreground/70">{s}</span>
                                        ))}
                                    </div>
                                </div>
                                <div className="p-6 pt-0 flex items-center justify-between">
                                    <span className="font-heading text-2xl font-extrabold">{p.price}</span>
                                    <Button asChild size="sm" className="rounded-full" data-testid={`program-detail-btn-${p.slug}`}>
                                        <Link to={`/programs/${p.slug}`}>Explore <ChevronRight className="ml-1 h-4 w-4" /></Link>
                                    </Button>
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* LEARNING PATHS */}
            <section className="section-pad bg-muted/40" id="paths">
                <div className="container-page">
                    <div className="max-w-2xl">
                        <p className="overline">Find Your Learning Path</p>
                        <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Age-wise learning, from ages 5 to 50+</h2>
                    </div>
                    <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {paths.map((p) => {
                            const Icon = PATH_ICONS[p.icon] || Sparkles;
                            return (
                                <Link key={p.id} to="/learning-paths" data-testid={`path-card-${p.age}`} className="group rounded-2xl bg-card border border-border p-6 hover:border-primary hover:shadow-lg transition-all">
                                    <div className="flex items-center justify-between">
                                        <span className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary"><Icon className="h-5 w-5" /></span>
                                        <span className="font-mono text-xs rounded-full border border-border px-2.5 py-1 text-foreground/60">Ages {p.age}</span>
                                    </div>
                                    <h3 className="mt-4 font-heading text-lg font-bold group-hover:text-primary transition-colors">{p.title}</h3>
                                    <p className="mt-2 text-sm text-foreground/60 leading-relaxed">{p.desc}</p>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* SEGMENTS: schools / corporate / community */}
            <section className="section-pad">
                <div className="container-page grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {[
                        { icon: GraduationCap, title: "For Schools", desc: "Institution-level dashboards, digital & AI readiness and a full digital curriculum for classes 1–12.", to: "/for-schools", color: "primary" },
                        { icon: Building2, title: "Corporate Academy", desc: "Upskill your workforce with C0–C8 capability levels and an Organizational AI Readiness Score (LAIRS).", to: "/corporate", color: "accent" },
                        { icon: Heart, title: "Community Mission", desc: "Empowering 1 million people through measurable digital literacy and practical education.", to: "/community", color: "success" },
                    ].map((s) => (
                        <Link key={s.title} to={s.to} data-testid={`segment-${s.title.toLowerCase().replace(/ /g, "-")}`} className="group relative overflow-hidden rounded-3xl bg-secondary text-secondary-foreground p-8 hover:shadow-2xl transition-all">
                            <span className={`grid h-12 w-12 place-items-center rounded-xl bg-${s.color}/20 text-${s.color === "primary" ? "primary" : s.color === "accent" ? "accent" : "success"}`}><s.icon className="h-6 w-6" /></span>
                            <h3 className="mt-5 font-heading text-xl font-bold">{s.title}</h3>
                            <p className="mt-2 text-sm text-secondary-foreground/70 leading-relaxed">{s.desc}</p>
                            <span className="mt-5 inline-flex items-center text-sm font-medium text-primary group-hover:gap-2 transition-all">Learn more <ArrowRight className="ml-1 h-4 w-4" /></span>
                        </Link>
                    ))}
                </div>
            </section>

            {/* DIGITAL PASSPORT */}
            <section className="section-pad bg-secondary text-secondary-foreground overflow-hidden">
                <div className="container-page grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <p className="overline text-accent">Digital Passport</p>
                        <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold">One verified record of everything you can do</h2>
                        <p className="mt-5 text-secondary-foreground/70 leading-relaxed">
                            Skills, projects, challenges, credentials and practical experience — all verifiable through a public verification page. No private data exposed.
                        </p>
                        <ul className="mt-6 space-y-3">
                            {["Verified skills & levels", "Mentor-reviewed project evidence", "Public credential verification"].map((f) => (
                                <li key={f} className="flex items-center gap-3 text-sm"><CheckCircle2 className="h-5 w-5 text-success shrink-0" /> {f}</li>
                            ))}
                        </ul>
                        <Button asChild size="lg" className="mt-8 rounded-full" data-testid="verify-cta-btn">
                            <Link to="/verify">Verify a Credential <ArrowRight className="ml-2 h-4 w-4" /></Link>
                        </Button>
                    </div>
                    <div className="rounded-3xl bg-white/5 border border-white/10 p-6 font-mono text-sm">
                        <div className="flex items-center justify-between border-b border-white/10 pb-4">
                            <span className="flex items-center gap-2 text-accent"><IdCard className="h-5 w-5" /> LARUTA Passport</span>
                            <Badge className="bg-success/20 text-success border-0">Active</Badge>
                        </div>
                        <div className="mt-4 space-y-2 text-secondary-foreground/80">
                            <p><span className="text-secondary-foreground/40">ID:</span> LARUTA-AIP-L2-2026-000127</p>
                            <p><span className="text-secondary-foreground/40">Level:</span> PX2 — Practice</p>
                            <p><span className="text-secondary-foreground/40">LPRS:</span> 68 / 100</p>
                            <p><span className="text-secondary-foreground/40">Skills:</span> AI Literacy · Prompting · Research</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="section-pad">
                <div className="container-page">
                    <p className="overline">Success Stories</p>
                    <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Learners who now have proof</h2>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
                        {tests.map((t) => (
                            <Card key={t.id} className="p-6 border-border/70">
                                <div className="flex gap-0.5 text-accent">{Array.from({ length: t.rating }).map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}</div>
                                <p className="mt-4 text-foreground/80 leading-relaxed">“{t.quote}”</p>
                                <div className="mt-5 flex items-center gap-3">
                                    <span className="grid h-10 w-10 place-items-center rounded-full bg-primary/10 text-primary font-heading font-bold">{t.name[0]}</span>
                                    <div>
                                        <p className="text-sm font-semibold">{t.name}</p>
                                        <p className="text-xs text-foreground/50">{t.role}</p>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="section-pad bg-muted/40">
                <div className="container-page max-w-3xl">
                    <p className="overline">FAQ</p>
                    <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Questions, answered</h2>
                    <Accordion type="single" collapsible className="mt-8">
                        {faqs.map((f) => (
                            <AccordionItem key={f.id} value={f.id} data-testid={`faq-${f.order}`}>
                                <AccordionTrigger className="text-left font-heading">{f.question}</AccordionTrigger>
                                <AccordionContent className="text-foreground/70 leading-relaxed">{f.answer}</AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            {/* CTA */}
            <section className="section-pad">
                <div className="container-page">
                    <div className="relative overflow-hidden rounded-3xl bg-primary text-primary-foreground p-10 sm:p-16 text-center">
                        <div className="absolute inset-0 grid-bg opacity-10" />
                        <div className="relative">
                            <h2 className="font-heading text-2xl sm:text-4xl font-extrabold">Ready to build real, demonstrable skills?</h2>
                            <p className="mt-4 text-primary-foreground/80 max-w-xl mx-auto">Start with a free digital skills assessment and find the learning path that fits you.</p>
                            <div className="mt-8 flex flex-wrap justify-center gap-3">
                                <Button asChild size="lg" variant="secondary" className="rounded-full h-12 px-6" data-testid="cta-programs-btn">
                                    <Link to="/programs">Explore Programs</Link>
                                </Button>
                                <LeadDialog source="free_assessment" title="Take a Free Digital Skills Assessment"
                                    trigger={<Button size="lg" className="rounded-full h-12 px-6 bg-secondary text-secondary-foreground hover:bg-secondary/90" data-testid="cta-assessment-btn">Free Assessment</Button>} />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </PublicShell>
    );
}
